// User-configurable mapping from notification-event categories → sound key.
// The default mapping mirrors the previously hardcoded TYPE_TO_SOUND table
// in useGlobalNotificationAlerts. Users can reassign a sound per category
// from the Sound Notifications settings card; the choice is persisted to
// localStorage under `lr.sound.event_map`.

const STORAGE_KEY = "lr.sound.event_map";
const EVENT_MAP_CHANGED = "lr-sound-event-map-changed";

/** Available sound choices (must exist as keys in notifySound's SOUNDS map). */
export const AVAILABLE_SOUNDS: { key: string; label: string }[] = [
  { key: "alert-ping",     label: "Generic ping" },
  { key: "dm-received",    label: "Direct message" },
  { key: "rfq-received",   label: "Quote request" },
  { key: "quote-received", label: "Broker quote" },
  { key: "bid-event",      label: "Bid / offer event" },
  { key: "bid-won",        label: "Load awarded / won" },
  { key: "private-load",   label: "Private load" },
];

/** User-facing event categories. Each rolls up 1+ notification.type values. */
export interface EventCategory {
  id: string;
  label: string;
  desc: string;
  types: string[];
  defaultSound: string;
}

export const EVENT_CATEGORIES: EventCategory[] = [
  {
    id: "dm",
    label: "Direct message",
    desc: "New DM in the messenger.",
    types: ["dm_received"],
    defaultSound: "dm-received",
  },
  {
    id: "quote_request",
    label: "Quote request (broker)",
    desc: "A shipper requested a quote.",
    types: ["quote_request_received"],
    defaultSound: "rfq-received",
  },
  {
    id: "quote_response",
    label: "Broker quote (shipper)",
    desc: "A broker submitted a quote / bid response.",
    types: ["quote_response_received", "direct_bid_received"],
    defaultSound: "quote-received",
  },
  {
    id: "bid_event",
    label: "Bid / offer event",
    desc: "Marketplace offers, counters, accepts, rejects.",
    types: [
      "new_offer",
      "counter_offer",
      "offer_accepted",
      "offer_rejected",
      "offer_accepted_by_carrier",
      "offer_rejected_by_carrier",
      "carrier_accepted",
    ],
    defaultSound: "bid-event",
  },
  {
    id: "bid_won",
    label: "Load awarded / won",
    desc: "You won a bid / were awarded a load.",
    types: ["bid_won", "load_awarded"],
    defaultSound: "bid-won",
  },
  {
    id: "private_load",
    label: "New private load",
    desc: "A private-network load was posted.",
    types: ["private_load_posted", "lr_direct_private_load"],
    defaultSound: "private-load",
  },
  {
    id: "open_load",
    label: "New open marketplace load",
    desc: "A new open-market load matched your feed.",
    types: ["new_open_load"],
    defaultSound: "private-load",
  },
  {
    id: "other",
    label: "Other alerts",
    desc: "Fallback for everything else.",
    types: [],
    defaultSound: "alert-ping",
  },
];

/** type → categoryId lookup, built once. */
const TYPE_TO_CATEGORY: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const c of EVENT_CATEGORIES) for (const t of c.types) m[t] = c.id;
  return m;
})();

type EventMap = Record<string, string>;

function readMap(): EventMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function getEventSoundMap(): EventMap {
  const overrides = readMap();
  const merged: EventMap = {};
  for (const c of EVENT_CATEGORIES) {
    merged[c.id] = overrides[c.id] || c.defaultSound;
  }
  return merged;
}

export function setEventSound(categoryId: string, soundKey: string) {
  if (typeof window === "undefined") return;
  const overrides = readMap();
  overrides[categoryId] = soundKey;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  window.dispatchEvent(new CustomEvent(EVENT_MAP_CHANGED));
}

export function subscribeEventMapChanges(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(EVENT_MAP_CHANGED, cb);
  return () => window.removeEventListener(EVENT_MAP_CHANGED, cb);
}

/** Resolve a notification.type value to the configured sound key. */
export function soundKeyForType(type: string | null | undefined): string {
  const map = getEventSoundMap();
  if (!type) return map.other ?? "alert-ping";
  const catId = TYPE_TO_CATEGORY[type] ?? "other";
  return map[catId] ?? "alert-ping";
}
